import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { connect } from "react-redux";
import styled, { createGlobalStyle } from "styled-components";
import NavBar from "../components/layout/NavBar";
import OuterContainer from "../components/layout/OuterContainer";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { request } from "../services/networking/request";
import TextInput from "../components/common/TextInput";
import Button from "../components/common/Button";
import Meta from "../components/Meta";
import colors from "../utils/constants/colors";
import { Eye, EyeOff, CheckCircle } from "lucide-react";

/* =============================== Global Styles =============================== */

const GlobalSpinStyles = createGlobalStyle`
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

/* =============================== Styling =============================== */

const AuthBg = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f9fc 0%, #f1f5fb 60%, #eef3fb 100%);
  padding-top: 64px;
`;

const ResetContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e9eef6;
  border-radius: 16px;
  margin: 48px auto;
  width: 460px;
  max-width: 95%;
  padding: 36px 32px;
  box-shadow: 0 12px 28px rgba(2, 8, 23, 0.06), 0 2px 8px rgba(2, 8, 23, 0.04);
`;

const Header = styled.h1`
  margin: 0 0 6px 0;
  text-align: center;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: #0f172a;
`;

const SubHeader = styled.p`
  margin: 0 0 24px 0;
  text-align: center;
  font-size: 14px;
  color: #64748b;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    color: ${colors.primary};
    background: rgba(59, 130, 246, 0.1);
  }
`;

const PasswordStrength = styled.div`
  margin: 8px 0;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${props => {
    if (props.strength === 'weak') return '#dc2626';
    if (props.strength === 'medium') return '#d97706';
    return '#16a34a';
  }};
`;

const StrengthBar = styled.div`
  flex: 1;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => {
      if (props.strength === 'weak') return '33%';
      if (props.strength === 'medium') return '66%';
      return '100%';
    }};
    background: ${props => {
      if (props.strength === 'weak') return '#dc2626';
      if (props.strength === 'medium') return '#d97706';
      return '#16a34a';
    }};
    transition: all 0.3s ease;
  }
`;

const ErrorMessage = styled.div`
  margin: 8px 0 12px;
  font-size: 13px;
  color: #b91c1c;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 8px 12px;
`;

const SuccessMessage = styled.div`
  margin: 8px 0 12px;
  font-size: 13px;
  color: #166534;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 18px;
  font-size: 14px;
  color: #475569;

  .link {
    color: ${colors.primary};
    font-weight: 700;
    cursor: pointer;
    margin-left: 6px;
  }
`;

/* =============================== Component =============================== */

const ResetPasswordWithToken = (props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [formValues, setFormValues] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('weak');

  useEffect(() => {
    if (!token) {
      setErrorMsg('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const getPasswordStrength = (password) => {
    if (password.length < 6) return 'weak';
    if (password.length < 10) return 'medium';
    if (password.length >= 10 && /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) return 'strong';
    return 'medium';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
    setErrorMsg('');

    if (name === 'password') {
      setPasswordStrength(getPasswordStrength(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setErrorMsg('Invalid reset token');
      return;
    }

    if (formValues.password.length < 6) {
      setErrorMsg('Password must be at least 6 characters long');
      return;
    }

    if (formValues.password !== formValues.confirmPassword) {
      setErrorMsg('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setErrorMsg('');

    try {
      const result = await request("POST", API_CONSTANT_MAP.reset_password, {
        token,
        password: formValues.password
      });

      if (result.data.success) {
        setIsSuccess(true);
        // Redirect to sign in after 3 seconds
        setTimeout(() => {
          navigate('/signin');
        }, 3000);
      }
    } catch (error) {
      setErrorMsg(error.error?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStrengthText = (strength) => {
    switch (strength) {
      case 'weak': return 'Weak';
      case 'medium': return 'Medium';
      case 'strong': return 'Strong';
      default: return 'Weak';
    }
  };

  if (isSuccess) {
    return (
      <Meta pageMeta={{ title: "Password Reset Successful - Doitly" }}>
        <GlobalSpinStyles />
        <NavBar user={props.state.user} />
        <AuthBg>
          <OuterContainer>
            <ResetContainer>
              <Header>Password Reset Successful!</Header>
              <SubHeader>
                Your password has been successfully updated.
              </SubHeader>
              
              <SuccessMessage>
                <CheckCircle size={16} />
                You will be redirected to sign in shortly...
              </SuccessMessage>

              <Button
                onClick={() => navigate('/signin')}
                style={{
                  margin: "18px 0 10px",
                  background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                  borderRadius: "10px",
                  fontWeight: 800,
                }}
              >
                Sign In Now
              </Button>
            </ResetContainer>
          </OuterContainer>
        </AuthBg>
      </Meta>
    );
  }

  return (
    <Meta pageMeta={{ title: "Reset Password - Doitly" }}>
      <GlobalSpinStyles />
      <NavBar user={props.state.user} />
      <AuthBg>
        <OuterContainer>
          <ResetContainer>
            <Header>Set New Password</Header>
            <SubHeader>
              Please enter your new password below
            </SubHeader>

            <form onSubmit={handleSubmit}>
              <PasswordInputWrapper>
                <TextInput
                  value={formValues.password}
                  name="password"
                  label="New Password"
                  onChange={handleInputChange}
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  disabled={!token}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </PasswordToggle>
              </PasswordInputWrapper>

              {formValues.password && (
                <PasswordStrength strength={passwordStrength}>
                  <StrengthBar strength={passwordStrength} />
                  Password strength: {getStrengthText(passwordStrength)}
                </PasswordStrength>
              )}

              <PasswordInputWrapper>
                <TextInput
                  value={formValues.confirmPassword}
                  name="confirmPassword"
                  label="Confirm New Password"
                  onChange={handleInputChange}
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  disabled={!token}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </PasswordToggle>
              </PasswordInputWrapper>

              <Button
                type="submit"
                disabled={isLoading || !token}
                style={{
                  margin: "18px 0 10px",
                  background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                  borderRadius: "10px",
                  fontWeight: 800,
                  opacity: (isLoading || !token) ? 0.7 : 1,
                }}
              >
                <span>
                  {isLoading && (
                    <span 
                      style={{
                        display: "inline-block",
                        marginRight: "8px",
                        animation: "spin 1s linear infinite"
                      }}
                    >
                      ⟳
                    </span>
                  )}
                  {isLoading ? "Resetting Password..." : "Reset Password"}
                </span>
              </Button>

              {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
            </form>

            <Footer>
              <span>Remember your password?</span>
              <span className="link" onClick={() => navigate('/signin')}>
                Back to Sign In
              </span>
            </Footer>
          </ResetContainer>
        </OuterContainer>
      </AuthBg>
    </Meta>
  );
};

const mapStateToProps = (state) => ({ state });

export default connect(mapStateToProps)(ResetPasswordWithToken);